import React from "react";
import styled from "styled-components";
import Hero from "src/components/Hero/Hero";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";
import SubTitleSection from "src/components/SubTitleSection/SubTitleSection";
import MarkdownWithRouter from "src/components/MarkdownWithRouter";
import EventFinished from "src/components/EventFinished";
import { includesAnyPhrase } from "src/utils/markdownText";

const resolveLangValue = (value, lang) => {
  if (typeof value === "string") {
    return value.replace("{lang}", lang).replace("{lng}", lang);
  }

  if (Array.isArray(value)) {
    return value.map((item) => resolveLangValue(item, lang));
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [
        key,
        resolveLangValue(nestedValue, lang),
      ])
    );
  }

  return value;
};

const CMS_BLOCK_MAP = {
  "dining-promo.hero": "Hero",
  "dining-promo.title-section": "TitleSection",
  "dining-promo.text-content": "TextContent",
  "dining-promo.small-text-content": "SmallTextContent",
  "dining-promo.image-section": "ImageSection",
  "dining-promo.split-section": "SplitSection",
  "dining-promo.stack-section": "StackSection",
  "dining-promo.choice-pair": "ChoicePair",
};

const normalizeSectionShape = (section) => {
  if (!section) {
    return null;
  }

  if (section.type) {
    return {
      type: section.type,
      props: section.props || {},
    };
  }

  if (section.__component) {
    const mappedType = CMS_BLOCK_MAP[section.__component];

    if (!mappedType) {
      console.warn(`Dining CMS block "${section.__component}" not found in registry`);
      return null;
    }

    const { __component, id, ...props } = section;
    return {
      type: mappedType,
      props,
    };
  }

  return null;
};

const normalizeSections = (sections = []) =>
  sections
    .map((section) => normalizeSectionShape(section))
    .filter(Boolean);

const buildLegacySections = (promo) => {
  if (!promo?.hero && !promo?.content) {
    return [];
  }

  const description = promo?.content?.description || promo?.content?.lunch?.description;
  const sideImage = promo?.content?.sideImage || promo?.content?.lunch?.image;
  const sections = [];

  if (promo?.hero) {
    sections.push({
      type: "Hero",
      props: {
        bgImage: promo.hero.bgImage,
        image: promo.hero.image,
        description: promo.hero.title,
        descriptionBgColor: promo.hero.descriptionBgColor || "#0000004d",
        darkenBg: 0,
        buttonSettings: null,
      },
    });
  }

  sections.push({
    type: "SplitSection",
    props: {
      withBackground: true,
      leftSections: [
        {
          type: "TitleSection",
          props: {
            as: "h2",
            subTitle: promo?.content?.subtitle,
          },
        },
        ...(description
          ? [
              {
                type: "TextContent",
                props: {
                  content: description,
                  className: "desc",
                },
              },
            ]
          : []),
        ...(promo?.content?.pgPlayersCard
          ? [
              {
                type: "TextContent",
                props: {
                  content: promo.content.pgPlayersCard,
                  routerAware: true,
                },
              },
            ]
          : []),
      ],
      rightSections: sideImage
        ? [
            {
              type: "ImageSection",
              props: {
                src: sideImage,
                alt: promo?.hero?.title || promo?.content?.subtitle,
              },
            },
          ]
        : [],
      rightColumnClassName: "pt-md-4",
    },
  });

  if (promo?.content?.smallText) {
    sections.push({
      type: "SmallTextContent",
      props: {
        content: promo.content.smallText,
      },
    });
  }

  return sections;
};

const DiningHeroSection = ({ ...props }) => <Hero {...props} />;

const DiningTitleSection = ({ as = "h2", title, subTitle, text }) => (
  <SubTitleSection
    as={as}
    subTitle={subTitle || title}
    text={text}
    className="dining-title-section"
  />
);

const DiningTextContent = ({
  content,
  routerAware = false,
  className = "",
  markdownVariant = "all-strong",
  strongHighlightPhrases = [],
  strongUnderlinePhrases = [],
}) => {
  if (!content) {
    return null;
  }

  if (routerAware) {
    return <MarkdownWithRouter className={className}>{content}</MarkdownWithRouter>;
  }

  if (markdownVariant !== "selective") {
    return <ReactMarkdown className={className}>{content}</ReactMarkdown>;
  }

  return (
    <ReactMarkdown
      className={className}
      components={{
        strong: ({ children, ...props }) => {
          const text = Array.isArray(children) ? children.join("") : String(children || "");
          const shouldHighlight = includesAnyPhrase(text, strongHighlightPhrases);
          const shouldUnderline = includesAnyPhrase(text, strongUnderlinePhrases);
          const strongClassName = [
            shouldHighlight ? "markdown-highlight" : "",
            shouldUnderline ? "markdown-underline" : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <strong className={strongClassName || undefined} {...props}>
              {children}
            </strong>
          );
        },
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

const DiningSmallTextContent = ({ content, className = "" }) => {
  if (!content) {
    return null;
  }

  const notes = (
    <container-div>
      <center-content>
        <ReactMarkdown className="small">{content}</ReactMarkdown>
      </center-content>
    </container-div>
  );

  // container-div is a custom element, so className does not become a CSS class.
  return className ? <div className={className}>{notes}</div> : notes;
};

const DiningImageSection = ({ src, alt }) => {
  if (!src) {
    return null;
  }

  return <ResponsiveImage src={src} alt={alt} />;
};

const DiningSplitSection = ({
  leftSections = [],
  rightSections = [],
  leftColumnClassName,
  rightColumnClassName,
  withBackground = true,
  backgroundColor,
  renderSection,
  sectionKey,
}) => (
  <MainContentSection
    $withBackground={withBackground}
    $backgroundColor={backgroundColor}
  >
    <container-div>
      <Row className="g-4 mb-5" xs={1} md={2}>
        <Col className={leftColumnClassName}>
          {normalizeSections(leftSections).map((section, index) =>
            renderSection(section, index, `${sectionKey}-left`)
          )}
        </Col>
        <Col className={rightColumnClassName}>
          {normalizeSections(rightSections).map((section, index) =>
            renderSection(section, index, `${sectionKey}-right`)
          )}
        </Col>
      </Row>
    </container-div>
  </MainContentSection>
);

const DiningStackSection = ({
  sections = [],
  withBackground = true,
  backgroundColor,
  className,
  renderSection,
  sectionKey,
}) => (
  <MainContentSection
    $withBackground={withBackground}
    $backgroundColor={backgroundColor}
    className={className}
  >
    <container-div>
      {normalizeSections(sections).map((section, index) =>
        renderSection(section, index, `${sectionKey}-stack`)
      )}
    </container-div>
  </MainContentSection>
);

const DiningChoicePair = ({
  leftImage,
  rightImage,
  arrowImage,
  leftAlt = "",
  rightAlt = "",
  arrowAlt = "",
}) => {
  if (!leftImage || !rightImage) {
    return null;
  }

  return (
    <ChoicePair>
      <ResponsiveImage src={leftImage} alt={leftAlt} className="choice-photo" />
      <ResponsiveImage src={rightImage} alt={rightAlt} className="choice-photo" />
      {arrowImage && (
        <ResponsiveImage src={arrowImage} alt={arrowAlt} className="choice-arrow" />
      )}
    </ChoicePair>
  );
};

const ChoicePair = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  align-items: stretch;
  width: 100%;
  margin: 0.5rem 0 1.25rem;

  .choice-photo {
    width: 100%;
    height: auto;
    border-radius: 1.15rem;
  }

  .choice-arrow {
    position: absolute;
    left: 50%;
    top: 50%;
    z-index: 1;
    width: 4.75rem;
    height: 4.75rem;
    object-fit: contain;
    transform: translate(-50%, -50%);
    pointer-events: none;
  }

  @media (max-width: 575.98px) {
    gap: 0.65rem;

    .choice-arrow {
      width: 3.25rem;
      height: 3.25rem;
    }
  }
`;

const COMPONENT_MAP = {
  Hero: DiningHeroSection,
  TitleSection: DiningTitleSection,
  TextContent: DiningTextContent,
  SmallTextContent: DiningSmallTextContent,
  ImageSection: DiningImageSection,
  SplitSection: DiningSplitSection,
  StackSection: DiningStackSection,
  ChoicePair: DiningChoicePair,
};

const DynamicDiningPromo = ({
  promoKey,
  scheduleKeyName,
  highlightColor,
  sections: propSections,
  theme: propTheme,
  promoData,
}) => {
  const { t, i18n } = useTranslation();
  const promo = promoData || (promoKey ? t(`diningPromos.${promoKey}`, { returnObjects: true }) : null);
  const theme = propTheme || promo?.theme || {};
  const resolvedHighlightColor = highlightColor || theme.highlightColor || "#d4bd55";
  const resolvedTitleHighlightColor = theme.titleHighlightColor || resolvedHighlightColor;
  const resolvedMarkdownHighlightColor =
    theme.markdownHighlightColor || resolvedHighlightColor;
  const resolvedMarkdownVariant = theme.markdownVariant || "all-strong";
  const rawSections = propSections || promo?.sections || buildLegacySections(promo);
  const sections = normalizeSections(resolveLangValue(rawSections, i18n.language));

  const renderSection = (section, index, parentKey = "root") => {
    const { type, props = {} } = section || {};
    const Component = COMPONENT_MAP[type];

    if (!Component) {
      console.warn(`Dining component type "${type}" not found in registry`);
      return null;
    }

    return (
      <Component
        key={`${parentKey}-${type}-${index}`}
        {...props}
        highlightColor={resolvedHighlightColor}
        markdownVariant={resolvedMarkdownVariant}
        renderSection={renderSection}
        sectionKey={`${parentKey}-${type}-${index}`}
      />
    );
  };

  return (
    <PromoPageDiv
      $titleHighlightColor={resolvedTitleHighlightColor}
      $markdownHighlightColor={resolvedMarkdownHighlightColor}
      $markdownVariant={resolvedMarkdownVariant}
    >
      {scheduleKeyName && (
        <EventFinished eventType="promotion" scheduleKeyName={scheduleKeyName} />
      )}
      {sections.map((section, index) => renderSection(section, index))}
    </PromoPageDiv>
  );
};

const PromoPageDiv = styled.div`
  width: 100%;
  height: auto;
  background: var(--lightblack);
  color: var(--white);
  padding-bottom: 1rem;

  .hero_image {
    padding: 2rem 2rem 4rem;
    opacity: 0.9;

    @media (min-width: 768px) {
      padding: 3rem;
    }
  }

  .dining-title-section {
    h2:after {
      border-color: ${(props) => props.$titleHighlightColor};
    }
  }

  .choice-stack .dining-title-section {
    text-align: center;

    h2:before {
      display: none;
    }

    h2:after {
      margin-left: auto;
      margin-right: auto;
    }
  }

  .choice-stack .step p {
    margin-bottom: 0.2rem;
  }

  .choice-stack .step-answer p {
    margin-bottom: 1.1rem;
  }

  .choice-follow {
    max-width: 860px;
    margin: 0.25rem auto 0;
    padding: 0 1.25rem;
    text-align: center;
  }

  .choice-follow p {
    margin-bottom: 1.35rem;
  }

  .choice-follow p:last-child {
    margin-bottom: 0;
  }

  .choice-stack {
    padding-bottom: 0;
  }

  .choice-notes {
    margin-top: 1.35rem;
    margin-bottom: 0;
  }

  .choice-notes .small p {
    margin-bottom: 0.65rem;
  }

  ${(props) =>
    props.$markdownVariant === "all-strong"
      ? `.desc strong {
    color: ${props.$markdownHighlightColor};
  }`
      : ""}

  .markdown-highlight {
    color: ${(props) => props.$markdownHighlightColor};
  }

  .markdown-underline {
    text-decoration: underline;
    text-underline-offset: 4px;
    text-decoration-thickness: 2px;
  }

  .small {
    text-align: center;
    max-width: 1100px;
    margin: 0 auto;
    word-wrap: break-word;
  }

  .players-choice-hero .hero_text_container h2 {
    margin: 0;
    line-height: 1.25;
    font-size: clamp(1.5rem, 3vw, 2rem);
    font-weight: 700;
  }

  @media (max-width: 767.98px) {
    .players-choice-hero .hero_text_container > div {
      text-wrap: balance;
    }

    .players-choice-hero .hero_text_container h2 {
      display: inline;
      font-size: clamp(1.35rem, 6.2vw, 1.75rem);
    }

    .players-choice-hero .hero_text_container h2:first-of-type::after {
      content: "\\00a0";
    }
  }

  .p_1 strong {
    white-space: nowrap;
  }

  p {
    margin-bottom: 1rem;
  }
`;

const MainContentSection = styled.div`
  padding-bottom: 1rem;

  h5 {
    margin-top: 2rem;
    font-weight: bold;
  }

  @media (min-width: 768px) {
    padding: 2rem 1rem 1rem;
  }

  container-div {
    background: ${(props) =>
      props.$withBackground
        ? props.$backgroundColor || "rgba(0, 0, 0, 0.5)"
        : "transparent"};
  }

  height: auto;
  width: 100%;
  text-align: left;
`;

export default DynamicDiningPromo;
export { COMPONENT_MAP, buildLegacySections };
